/*
 * Logistics API client — thin wrapper around fetch.
 * Uses REACT_APP_BACKEND_URL, sends JWT from localStorage.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND}/api/logistics`;

// True when the API lives on the page's own origin (the deployed setup, where
// the ingress serves app and API together). Local dev runs the CRA server on
// :3000 and uvicorn on :8001, so it's false there.
const SAME_ORIGIN = (() => {
  if (!BACKEND) return true;
  try {
    return new URL(BACKEND, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
})();

const headers = (extra = {}) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('ems_token')}`,
  ...extra,
});

const jsonOrThrow = async (res) => {
  if (!res.ok) {
    let msg = 'Request failed';
    try { const j = await res.json(); msg = j.detail || j.message || msg; } catch {/* noop */}
    throw new Error(msg);
  }
  return res.json();
};

export const logisticsApi = {
  // Exercises
  listExercises: () => fetch(`${API}/exercises`, { headers: headers() }).then(jsonOrThrow),
  getExercise: (id) => fetch(`${API}/exercises/${id}`, { headers: headers() }).then(jsonOrThrow),
  createExercise: (body) => fetch(`${API}/exercises`, { method: 'POST', headers: headers(), body: JSON.stringify(body) }).then(jsonOrThrow),
  bootstrapExercise: (body) => fetch(`${API}/exercises/bootstrap`, { method: 'POST', headers: headers(), body: JSON.stringify(body) }).then(jsonOrThrow),
  updateExercise: (id, body) => fetch(`${API}/exercises/${id}`, { method: 'PATCH', headers: headers(), body: JSON.stringify(body) }).then(jsonOrThrow),
  archiveExercise: (id) => fetch(`${API}/exercises/${id}`, { method: 'DELETE', headers: headers() }).then(jsonOrThrow),
  activateExercise: (id) => fetch(`${API}/exercises/${id}/activate`, { method: 'POST', headers: headers() }).then(jsonOrThrow),
  pauseExercise: (id) => fetch(`${API}/exercises/${id}/pause`, { method: 'POST', headers: headers() }).then(jsonOrThrow),
  endExercise: (id) => fetch(`${API}/exercises/${id}/end`, { method: 'POST', headers: headers() }).then(jsonOrThrow),
  hardDeleteExercise: (id) => fetch(`${API}/exercises/${id}/hard-delete`, { method: 'POST', headers: headers() }).then(jsonOrThrow),
  resetExercise: (id) => fetch(`${API}/exercises/${id}/reset`, { method: 'POST', headers: headers() }).then(jsonOrThrow),
  hubUsers: () => fetch(`${API}/hub-users`, { headers: headers() }).then(jsonOrThrow),
  emsExercises: () => fetch(`${API}/ems-exercises`, { headers: headers() }).then(jsonOrThrow),
  mpExercises: () => fetch(`${API}/mp-exercises`, { headers: headers() }).then(jsonOrThrow),

  // Wallets
  myWallets: () => fetch(`${API}/wallets`, { headers: headers() }).then(jsonOrThrow),
  walletsForExercise: (exId) => fetch(`${API}/exercises/${exId}/wallets`, { headers: headers() }).then(jsonOrThrow),
  getWallet: (walletId) => fetch(`${API}/wallets/${walletId}`, { headers: headers() }).then(jsonOrThrow),
  walletTransactions: (walletId) => fetch(`${API}/wallets/${walletId}/transactions`, { headers: headers() }).then(jsonOrThrow),
  allocateWallet: (exId, body) => fetch(`${API}/exercises/${exId}/wallets/allocate`, { method: 'POST', headers: headers(), body: JSON.stringify(body) }).then(jsonOrThrow),
  topupWallet: (walletId, body) => fetch(`${API}/wallets/${walletId}/topup`, { method: 'POST', headers: headers(), body: JSON.stringify(body) }).then(jsonOrThrow),
  deductWallet: (walletId, body) => fetch(`${API}/wallets/${walletId}/deduct`, { method: 'POST', headers: headers(), body: JSON.stringify(body) }).then(jsonOrThrow),
  freezeWallet: (walletId) => fetch(`${API}/wallets/${walletId}/freeze`, { method: 'POST', headers: headers() }).then(jsonOrThrow),
  unfreezeWallet: (walletId) => fetch(`${API}/wallets/${walletId}/unfreeze`, { method: 'POST', headers: headers() }).then(jsonOrThrow),

  // Venues (P1a — real OSM data + admin overlay)
  searchVenues: async (exId, body) => {
    console.log('[logisticsApi] searchVenues → START', exId);
    // Give up if the request hangs — surfaces the real cause instead of an
    // eternal "Searching…" pill.
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 25000);
    try {
      // Prefer a RELATIVE path — the Kubernetes ingress-proxied preview URL
      // sometimes stalls absolute-URL POSTs to /api/* that carry a body >~4KB.
      // That only works when the API shares the page's origin, so fall back to
      // the absolute URL for local dev (CRA on :3000, uvicorn on :8001), where
      // a relative path would hit the dev server instead of the API.
      const searchUrl = SAME_ORIGIN
        ? `/api/logistics/exercises/${exId}/venues/search`
        : `${API}/exercises/${exId}/venues/search`;
      const r = await fetch(searchUrl, {
        method: 'POST', headers: headers(), body: JSON.stringify(body),
        signal: controller.signal,
      });
      console.log('[logisticsApi] searchVenues ← RESP', r.status);
      const j = await jsonOrThrow(r);
      console.log('[logisticsApi] searchVenues ← JSON count=', j.count);
      return j;
    } catch (e) {
      console.log('[logisticsApi] searchVenues !! CAUGHT', e.name, e.message);
      throw e;
    } finally {
      clearTimeout(t);
    }
  },
  venueCategories: (exId) => fetch(`${API}/exercises/${exId}/venues/categories`, { headers: headers() }).then(jsonOrThrow),

  // Accommodation bookings (P1b — quote → hold → capture → release/refund)
  quoteAccommodation: (exId, body) => fetch(`${API}/exercises/${exId}/accommodation/quote`, {
    method: 'POST', headers: headers(), body: JSON.stringify(body),
  }).then(jsonOrThrow),
  createBooking: (exId, body) => fetch(`${API}/exercises/${exId}/bookings`, {
    method: 'POST', headers: headers(), body: JSON.stringify(body),
  }).then(jsonOrThrow),
  confirmBooking: (bookingId) => fetch(`${API}/bookings/${bookingId}/confirm`, {
    method: 'POST', headers: headers(),
  }).then(jsonOrThrow),
  cancelBooking: (bookingId, body = {}) => fetch(`${API}/bookings/${bookingId}/cancel`, {
    method: 'POST', headers: headers(), body: JSON.stringify(body),
  }).then(jsonOrThrow),
  myBookings: (exId) => fetch(`${API}/bookings${exId ? `?exercise_id=${encodeURIComponent(exId)}` : ''}`, {
    headers: headers(),
  }).then(jsonOrThrow),
  bookingsForExercise: (exId) => fetch(`${API}/exercises/${exId}/bookings`, { headers: headers() }).then(jsonOrThrow),
  getBooking: (bookingId) => fetch(`${API}/bookings/${bookingId}`, { headers: headers() }).then(jsonOrThrow),
  walletSimCard: (walletId) => fetch(`${API}/wallets/${walletId}/sim-card`, { headers: headers() }).then(jsonOrThrow),
  walletMembers: (walletId) => fetch(`${API}/wallets/${walletId}/members`, { headers: headers() }).then(jsonOrThrow),
  walletCards: (walletId) => fetch(`${API}/wallets/${walletId}/cards`, { headers: headers() }).then(jsonOrThrow),
  cardTransactions: (walletId, cardId) => fetch(`${API}/wallets/${walletId}/transactions/${encodeURIComponent(cardId)}`, { headers: headers() }).then(jsonOrThrow),

  // Venue detail (rich panel data)
  venueDetail: (exId, venueId) => fetch(`${API}/exercises/${exId}/venues/${venueId}/detail`, { headers: headers() }).then(jsonOrThrow),

  // Admin venue CRUD
  createVenue: (exId, body) => fetch(`${API}/exercises/${exId}/venues`, {
    method: 'POST', headers: headers(), body: JSON.stringify(body),
  }).then(jsonOrThrow),
  updateVenue: (venueId, body) => fetch(`${API}/venues/${venueId}`, {
    method: 'PATCH', headers: headers(), body: JSON.stringify(body),
  }).then(jsonOrThrow),
  deleteVenue: (venueId) => fetch(`${API}/venues/${venueId}`, {
    method: 'DELETE', headers: headers(),
  }).then(jsonOrThrow),
  overrideVenue: (exId, venueId, body) => fetch(`${API}/exercises/${exId}/venues/${venueId}/override`, {
    method: 'POST', headers: headers(), body: JSON.stringify(body),
  }).then(jsonOrThrow),
  removeOverride: (exId, venueId) => fetch(`${API}/exercises/${exId}/venues/${venueId}/override`, {
    method: 'DELETE', headers: headers(),
  }).then(jsonOrThrow),
  uploadVenueImage: (venueId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API}/venues/${venueId}/images`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('ems_token')}` },
      body: fd,
    }).then(jsonOrThrow);
  },

  // Browse (card listing)
  browseVenues: (exId, filters) => {
    const url = SAME_ORIGIN
      ? `/api/logistics/exercises/${exId}/venues/browse`
      : `${API}/exercises/${exId}/venues/browse`;
    return fetch(url, {
      method: 'POST', headers: headers(), body: JSON.stringify(filters),
    }).then(jsonOrThrow);
  },
  presetCities: (exId) => fetch(`${API}/exercises/${exId}/venues/preset-cities`, { headers: headers() }).then(jsonOrThrow),

  // Food ordering (V15.4)
  foodVenueDetail: (exId, venueId) => fetch(`${API}/exercises/${exId}/food/${venueId}/detail`, { headers: headers() }).then(jsonOrThrow),
  quoteFood: (exId, body) => fetch(`${API}/exercises/${exId}/food/quote`, {
    method: 'POST', headers: headers(), body: JSON.stringify(body),
  }).then(jsonOrThrow),
  createFoodOrder: (exId, body) => fetch(`${API}/exercises/${exId}/food/orders`, {
    method: 'POST', headers: headers(), body: JSON.stringify(body),
  }).then(jsonOrThrow),
  confirmFoodOrder: (orderId) => fetch(`${API}/food/orders/${orderId}/confirm`, {
    method: 'POST', headers: headers(),
  }).then(jsonOrThrow),
  cancelFoodOrder: (orderId, body = {}) => fetch(`${API}/food/orders/${orderId}/cancel`, {
    method: 'POST', headers: headers(), body: JSON.stringify(body),
  }).then(jsonOrThrow),
  myFoodOrders: (exId) => fetch(`${API}/food/orders${exId ? `?exercise_id=${encodeURIComponent(exId)}` : ''}`, {
    headers: headers(),
  }).then(jsonOrThrow),
  foodOrdersForExercise: (exId) => fetch(`${API}/exercises/${exId}/food/orders`, { headers: headers() }).then(jsonOrThrow),

  // Transport (V15.4)
  estimateRoute: (exId, body) => fetch(`${API}/exercises/${exId}/transport/route`, {
    method: 'POST', headers: headers(), body: JSON.stringify(body),
  }).then(jsonOrThrow),
  transportVenueDetail: (exId, venueId) => fetch(`${API}/exercises/${exId}/transport/${venueId}/detail`, { headers: headers() }).then(jsonOrThrow),
  quoteTransport: (exId, body) => fetch(`${API}/exercises/${exId}/transport/quote`, {
    method: 'POST', headers: headers(), body: JSON.stringify(body),
  }).then(jsonOrThrow),
  createTransportBooking: (exId, body) => fetch(`${API}/exercises/${exId}/transport/bookings`, {
    method: 'POST', headers: headers(), body: JSON.stringify(body),
  }).then(jsonOrThrow),
  confirmTransportBooking: (bookingId) => fetch(`${API}/transport/bookings/${bookingId}/confirm`, {
    method: 'POST', headers: headers(),
  }).then(jsonOrThrow),
  cancelTransportBooking: (bookingId, body = {}) => fetch(`${API}/transport/bookings/${bookingId}/cancel`, {
    method: 'POST', headers: headers(), body: JSON.stringify(body),
  }).then(jsonOrThrow),
  myTransportBookings: (exId) => fetch(`${API}/transport/bookings${exId ? `?exercise_id=${encodeURIComponent(exId)}` : ''}`, {
    headers: headers(),
  }).then(jsonOrThrow),
  transportBookingsForExercise: (exId) => fetch(`${API}/exercises/${exId}/transport/bookings`, { headers: headers() }).then(jsonOrThrow),

  geocodeSearch: (exId, query) => {
    const url = SAME_ORIGIN
      ? `/api/logistics/exercises/${exId}/venues/geocode?q=${encodeURIComponent(query)}`
      : `${API}/exercises/${exId}/venues/geocode?q=${encodeURIComponent(query)}`;
    return fetch(url, { headers: headers() }).then(jsonOrThrow);
  },
};

export default logisticsApi;
